import { useState } from 'react';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import Card from '@/components/UI/Card';
import Button from '@/components/UI/Button';
import type { OptionLiquidityDiagnostic } from '@einhaus/project314-types';

export function optionLiquidityMessages(diagnostic: OptionLiquidityDiagnostic): string[] {
    const messages: string[] = [];
    if (diagnostic.zeroBidExit) messages.push('Zero-bid exit: modeled sale at $0.');
    if (diagnostic.evidenceStatus !== 'matched') {
        messages.push(
            diagnostic.evidenceStatus === 'missing'
                ? 'Quote unavailable; displayed size could not be checked.'
                : 'Quote does not match the saved fill; displayed size could not be checked.'
        );
    } else if (diagnostic.exceedsDisplayedSize) {
        messages.push(`${diagnostic.fill.quantity} contracts modeled against ${diagnostic.displayedSize} displayed.`);
    }
    return messages;
}

export default function BacktestOptionLiquidity({ backtestId, tradeIds }: { backtestId: number; tradeIds: number[] }) {
    const [enabled, setEnabled] = useState(false);
    const query = useAppQuery(
        {
            ...trpc.backtest.optionLiquidityDiagnostics.queryOptions({ backtestId, tradeIds }),
            enabled: enabled && tradeIds.length > 0
        },
        { fallbackErrorMessage: 'Could not check option liquidity', showErrorToast: true }
    );
    const diagnostics = query.data?.diagnostics ?? [];
    const warnings = diagnostics
        .map((diagnostic) => ({ diagnostic, messages: optionLiquidityMessages(diagnostic) }))
        .filter((row) => row.messages.length > 0);
    return (
        <Card className="mb-4 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h5>Option liquidity</h5>
                <Button
                    disabled={enabled && query.isFetching}
                    onClick={() => {
                        setEnabled(true);
                        if (enabled) void query.refetch();
                    }}
                >
                    {enabled && query.isFetching ? 'Checking…' : 'Check this page'}
                </Button>
            </div>
            <p className="mt-2 text-sm text-zinc-400">
                Compares modeled contracts with displayed quote size and flags zero-bid exits. Quote size is a snapshot, not proof of a full
                fill. This check does not change trades or spread limits.
            </p>
            {enabled && query.isError && (
                <p role="alert" className="mt-3 text-red-400">
                    Liquidity check failed. Results are unavailable.
                </p>
            )}
            {enabled && query.data && !query.isError && (
                <>
                    <p className="mt-3 text-sm">
                        {diagnostics.length} option transactions checked on this page; {warnings.length} need review.
                    </p>
                    {warnings.length > 0 && (
                        <ul className="mt-2 space-y-2 text-sm">
                            {warnings.map(({ diagnostic, messages }) => (
                                <li key={diagnostic.fill.transactionId}>
                                    <span className="font-medium">
                                        Trade {diagnostic.fill.tradeId} · {diagnostic.fill.transactionType}:{' '}
                                    </span>
                                    {messages.join(' ')}
                                </li>
                            ))}
                        </ul>
                    )}
                </>
            )}
        </Card>
    );
}
